import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SystemHealthMonitor = () => {
  const [healthMetrics, setHealthMetrics] = useState({
    systemStatus: 'healthy',
    uptime: '15d 8h 42m',
    cpuUsage: 45,
    memoryUsage: 62,
    diskUsage: 38,
    activeUsers: 127,
    databaseConnections: 85,
    apiResponseTime: 245,
    lastUpdated: new Date()?.toISOString()
  });

  const [integrationHealth, setIntegrationHealth] = useState([
    {
      name: "SAP ERP",
      status: "healthy",
      responseTime: 180,
      lastCheck: new Date(Date.now() - 2 * 60 * 1000)?.toISOString(),
      uptime: 99.8
    },
    {
      name: "Active Directory",
      status: "healthy",
      responseTime: 95,
      lastCheck: new Date(Date.now() - 1 * 60 * 1000)?.toISOString(),
      uptime: 99.9
    },
    {
      name: "Barcode Scanner API",
      status: "warning",
      responseTime: 850,
      lastCheck: new Date(Date.now() - 5 * 60 * 1000)?.toISOString(),
      uptime: 97.2
    },
    {
      name: "Procurement Portal",
      status: "error",
      responseTime: 0,
      lastCheck: new Date(Date.now() - 15 * 60 * 1000)?.toISOString(),
      uptime: 94.5
    }
  ]);

  const [recentAlerts, setRecentAlerts] = useState([
    {
      id: 1,
      type: "warning",
      message: "High memory usage detected (>80%)",
      timestamp: new Date(Date.now() - 10 * 60 * 1000)?.toISOString(),
      resolved: false
    },
    {
      id: 2,
      type: "error",
      message: "Procurement Portal connection failed",
      timestamp: new Date(Date.now() - 15 * 60 * 1000)?.toISOString(),
      resolved: false
    },
    {
      id: 3,
      type: "info",
      message: "Scheduled backup completed successfully",
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000)?.toISOString(),
      resolved: true
    }
  ]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setHealthMetrics(prev => ({
        ...prev,
        cpuUsage: Math.max(20, Math.min(80, prev?.cpuUsage + (Math.random() - 0.5) * 10)),
        memoryUsage: Math.max(30, Math.min(90, prev?.memoryUsage + (Math.random() - 0.5) * 8)),
        diskUsage: Math.max(20, Math.min(70, prev?.diskUsage + (Math.random() - 0.5) * 5)),
        activeUsers: Math.max(50, Math.min(200, prev?.activeUsers + Math.floor((Math.random() - 0.5) * 20))),
        apiResponseTime: Math.max(100, Math.min(500, prev?.apiResponseTime + (Math.random() - 0.5) * 50)),
        lastUpdated: new Date()?.toISOString()
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'AlertCircle';
      default:
        return 'Clock';
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 80) return 'bg-error';
    if (percentage >= 60) return 'bg-warning';
    return 'bg-success';
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setHealthMetrics(prev => ({
      ...prev,
      lastUpdated: new Date()?.toISOString()
    }));
    
    setIsRefreshing(false);
  };

  const handleResolveAlert = (alertId) => {
    setRecentAlerts(prev =>
      prev?.map(alert =>
        alert?.id === alertId ? { ...alert, resolved: true } : alert
      )
    );
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return date?.toLocaleDateString();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">System Health Monitor</h3>
          <p className="text-sm text-muted-foreground">Real-time system performance and integration status</p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          loading={isRefreshing}
          iconName="RefreshCw"
          iconPosition="left"
        >
          Refresh
        </Button>
      </div>
      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">System Status</span>
            <Icon name={getStatusIcon(healthMetrics?.systemStatus)} size={16} className={getStatusColor(healthMetrics?.systemStatus)} />
          </div>
          <p className="text-lg font-semibold text-card-foreground capitalize">{healthMetrics?.systemStatus}</p>
          <p className="text-xs text-muted-foreground">Uptime: {healthMetrics?.uptime}</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">CPU Usage</span>
            <Icon name="Cpu" size={16} className="text-muted-foreground" />
          </div>
          <p className="text-lg font-semibold text-card-foreground">{healthMetrics?.cpuUsage}%</p>
          <div className="w-full bg-muted rounded-full h-2 mt-2">
            <div 
              className={`h-2 rounded-full transition-all ${getUsageColor(healthMetrics?.cpuUsage)}`}
              style={{ width: `${healthMetrics?.cpuUsage}%` }}
            />
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Memory Usage</span>
            <Icon name="HardDrive" size={16} className="text-muted-foreground" />
          </div>
          <p className="text-lg font-semibold text-card-foreground">{healthMetrics?.memoryUsage}%</p>
          <div className="w-full bg-muted rounded-full h-2 mt-2">
            <div 
              className={`h-2 rounded-full transition-all ${getUsageColor(healthMetrics?.memoryUsage)}`}
              style={{ width: `${healthMetrics?.memoryUsage}%` }}
            />
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Active Users</span>
            <Icon name="Users" size={16} className="text-muted-foreground" />
          </div>
          <p className="text-lg font-semibold text-card-foreground">{healthMetrics?.activeUsers}</p>
          <p className="text-xs text-muted-foreground">API Response: {healthMetrics?.apiResponseTime}ms</p>
        </div>
      </div>
      {/* Integration Health */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Zap" size={20} className="text-primary" />
          <h4 className="text-lg font-semibold text-card-foreground">Integration Health</h4>
        </div>
        
        <div className="space-y-3">
          {integrationHealth?.map((integration, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name={getStatusIcon(integration?.status)} size={16} className={getStatusColor(integration?.status)} />
                <div>
                  <p className="font-medium text-card-foreground">{integration?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Last check: {formatTimestamp(integration?.lastCheck)}
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-sm font-medium text-card-foreground">
                  {integration?.responseTime > 0 ? `${integration?.responseTime}ms` : 'Offline'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {integration?.uptime}% uptime
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Alerts */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Bell" size={20} className="text-primary" />
            <h4 className="text-lg font-semibold text-card-foreground">Recent Alerts</h4>
          </div>
          <Button variant="ghost" size="sm">
            View All
          </Button>
        </div>
        
        <div className="space-y-3">
          {recentAlerts?.map((alert) => (
            <div key={alert?.id} className={`flex items-start justify-between p-3 rounded-lg ${
              alert?.resolved ? 'bg-muted/30' : 'bg-warning/10'
            }`}>
              <div className="flex items-start space-x-3">
                <Icon 
                  name={alert?.type === 'error' ? 'AlertCircle' : alert?.type === 'warning' ? 'AlertTriangle' : 'Info'} 
                  size={16} 
                  className={alert?.type === 'error' ? 'text-error' : alert?.type === 'warning' ? 'text-warning' : 'text-accent'}
                />
                <div>
                  <p className={`text-sm ${alert?.resolved ? 'text-muted-foreground' : 'text-card-foreground'}`}>
                    {alert?.message}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTimestamp(alert?.timestamp)}
                  </p>
                </div>
              </div>
              
              {!alert?.resolved && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleResolveAlert(alert?.id)}
                  iconName="Check"
                >
                  Resolve
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Last Updated */}
      <div className="flex items-center justify-center text-sm text-muted-foreground">
        <Icon name="Clock" size={14} className="mr-2" />
        <span>Last updated: {formatTimestamp(healthMetrics?.lastUpdated)}</span>
      </div>
    </div>
  );
};

export default SystemHealthMonitor;